import { useEffect, useState } from 'react';

import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { DialogLoader } from '../../../../components/Dialog/DialogLoader';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { I18Text } from '../../../../components/i18Next/I18Text';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { auth } from '../../../../services/auth';
import { useStores } from '../../../../stores/useStores';
import { getErrorMessage } from '../../../../utils/errors';
import { EmailVerificationSuccessRedesign } from '../EmailVerificationSuccessRedesign/EmailVerificationSuccessRedesign';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';

import styles from './EmailVerificationRedesign.module.scss';
import envelope from './mail_with_crown_v2.png';

export const EmailVerificationRedesign = observer(function EmailVerificationRedesign({
  isCurrent,
  prevStep,
  nextStep,
}: WizardChildProps) {
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { authStore, portalStore } = useStores();

  const { t } = useTranslation();

  const { sendVerificationEmailCooldown } = authStore;

  const [error, setError] = useState<string>('');

  const isProcessing = computed(() => authStore.pending('resendEmailVerification')).get();

  const handleGoBack = async () => {
    await auth.signOut();
    prevStep();
  };

  const handleClose = async () => {
    await auth.signOut();
    portalStore.closePortal();
  };

  const getClosingProps = () => {
    if (showSuccessAnimation && isDesktop) {
      return {};
    }

    if (isDesktop) {
      return { onClose: handleClose };
    }
    return { onGoBack: handleGoBack };
  };

  const handleResend = async () => {
    try {
      if (sendVerificationEmailCooldown > 0) {
        return;
      }
      await authStore.resendEmailVerification();
    } catch (e) {
      const msg = getErrorMessage(e);
      setError(msg);
    }
  };

  useEffect(() => {
    if (isCurrent) {
      void analytics.sendEvent('email_account_check_email', {
        event_category: 'sign_up',
        event_action: 'impression',
      });

      const unsubscribe = authStore.trackEmailVerificationCompletion(() => {
        void analytics.sendEvent('create_account_success', {
          event_category: 'sign_up',
          event_action: 'impression',
          event_type: 'signup_platform',
          event_value: 'email',
        });

        setShowSuccessAnimation(true);
      });

      return () => unsubscribe();
    }
  }, [authStore, isCurrent]);

  useEffect(() => {
    return () => authStore.clearSendVerificationEmailCooldown();
  }, [authStore]);

  return (
    <RedesignContainer className={styles.container}>
      <DialogHeader
        className={styles.dialogHeader}
        closeBtnDataTestId="confirmation-close-btn"
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="confirmation-back-btn"
        title={t('emailVerification.title')}
        {...getClosingProps()}
      />

      <DialogLoader isProcessing={isProcessing} />

      <div className={styles.containerContent}>
        <div className={styles.topContainer}>
          <I18Text
            className={styles.regularText}
            components={{
              strong: <span className={styles.textGreenBold} />,
            }}
            i18nKey="emailVerification.emailVerifyWelcomeBonus"
            testid="email-verification-title"
          />

          <img alt="envelope" className={styles.envelopeImage} src={envelope} />

          <p className={styles.regularText}>
            {t('emailVerification.confirmationEmailSent')}{' '}
            <b className={styles.textEmail} data-testid="confirmation-user-mail">
              {authStore.currentUserEmail}
            </b>
          </p>
        </div>

        <p className={styles.emailArrivalHint}>{t('emailVerification.emailArrivalHint')}</p>

        <p className={styles.resendRow}>
          {t('emailVerification.didntReceive')}
          {isProcessing ? (
            <span className={styles.resending}>{t('emailVerification.resending')}</span>
          ) : (
            <span
              className={classNames(styles.resendLink, {
                [styles.resendLinkDisabled]: sendVerificationEmailCooldown > 0,
              })}
              data-testid="confirmation-resend-email-btn"
              onClick={handleResend}
            >
              {sendVerificationEmailCooldown > 0
                ? t('emailVerification.resendEmailWithSeconds', { seconds: sendVerificationEmailCooldown })
                : t('emailVerification.resendEmail')}
            </span>
          )}
        </p>
        <ErrorMessage>{error}</ErrorMessage>
      </div>
      <div
        className={classNames(styles.successAnimationContainer, {
          [styles.successAnimationContainerHidden]: !showSuccessAnimation,
        })}
      >
        <EmailVerificationSuccessRedesign isCurrent={showSuccessAnimation} nextStep={nextStep} />
      </div>
    </RedesignContainer>
  );
});
